import { Box } from "@mui/material";
import { useEffect } from "react";
import type { ReactNode } from "react";

import { roomTypeLabel } from "../room-path";
import { type RoomNavProps, RoomSidebar, SIDEBAR_MOBILE_BAR_HEIGHT } from "./RoomSidebar";

// Wraps a single room page with the persistent left drawer (RoomSidebar) and a
// scrolling main column. Used by both the preview route and the production room
// route — they only differ in `basePath`. On mobile the sidebar collapses to an
// off-canvas drawer behind a fixed top bar, so main gets a top offset there.
export interface RoomLayoutProps extends RoomNavProps {
    children: ReactNode;
}

// The browser tab reads "<Supplier> | Pitch Room" (or Deal Room, Content
// Optimization Room) from the room's type, never the static index.html title
// (Bill, 2026-09-24: a pitch room said "Deal Room").
function titleCase(s: string) {
    return s
        .split(" ")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
}

export function RoomLayout({ children, ...nav }: RoomLayoutProps) {
    const { roomType } = nav.room;
    const supplierName = nav.room.supplier.supplierName;
    useEffect(() => {
        document.title = `${supplierName} | ${titleCase(roomTypeLabel(roomType))}`;
    }, [supplierName, roomType]);
    return (
        <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
            <RoomSidebar {...nav} />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    display: "flex",
                    flexDirection: "column",
                    pt: { xs: `${SIDEBAR_MOBILE_BAR_HEIGHT}px`, md: 0 },
                }}
            >
                {children}
            </Box>
        </Box>
    );
}
